import { PropType } from 'vue'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { BModal } from 'bootstrap-vue'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'

import { Item, SearchResult } from '@skinsoft/types'
import { itemSelectors } from '@skinsoft/selectors'
import { searchResultSelectors } from '@skinsoft/search-data-store'

import { ReactiveVue } from '@skinsoft/reactive-vue'
import { LINK_MOVEMENT_PAGE_NAVIGATE, SELECT_ITEM_MULTIPLE_MODAL_MULTIPLE_ITEMS_SELECTED } from '../../../events'
import { SelectItemMultipleForm } from './select-item-multiple-form'
import { BarCodeEditor } from '../../../app/component/action-bar/map-icon-toggle/bar-code-editor'

require('./select-item-multiple-modal.scss')

export interface SIMMItemSelected {
    id: string,
    type: string,
    item: SearchResult | Item,
}

@Component({
    components: {
        sSelectItemMultipleForm: SelectItemMultipleForm,
        sBarCodeEditor: BarCodeEditor,
    },
    props: {
        canChainAdd: {
            type: Boolean,
            default: true
        },
        isUpdating: Boolean,
        canChangeMode: {
            type: Boolean,
            default: false,
        },
        showLinksToMovements: {
            type: Boolean,
            default: false
        },
        excludedIds: {
            type: Array as PropType<string[]>,
            default: () => [],
        },
        excludedItemsWarningMessageKey: {
            type: String,
        },
        types: Array as PropType<string[]>,
        emitChainAddResultsTogether: {
            type: Boolean,
            default: false,
        },
        maxNbChainAdd: {
            type: Number,
            default: 50,
        }
    },
    template: require('./select-item-multiple-modal.html'),
})
export class SelectItemMultipleModal extends ReactiveVue {
    public mounted = false
    public spinnerIcon = faCircleNotch
    public locationSelector = true

    public canChainAdd: boolean
    public isUpdating: boolean
    public canChangeMode: boolean
    public types: Array<string>
    private showLinksToMovements: boolean
    private excludedIds: string[]
    private excludedItemsWarningMessageKey: string
    private emitChainAddResultsTogether: boolean
    private maxNbChainAdd: number

    public shouldChainAdd: boolean = false
    private selectedResults: (Item | SearchResult)[] = [] // for emitting all results at once when chain addding
    private isLocallyUpdating: boolean = false

    private LINK_MOVEMENT_PAGE_NAVIGATE = LINK_MOVEMENT_PAGE_NAVIGATE


    public $refs: {
        modal: BModal
        itemSelect: SelectItemMultipleForm
    }

    public open() {
        this.$refs.modal.show()
    }

    public close() {
        this.$refs.modal.hide()
    }

    public onShow() {
        this.mounted = true
    }

    public onHide() {
        this.emitMultipleItemsSelected()
        this.mounted = false
    }

    private emitMultipleItemsSelected() {
        if(this.emitChainAddResultsTogether && this.selectedResults.length) {
            const items: SIMMItemSelected[] = this.selectedResults.map(item => {
                return { 
                    id: (item as any).itemId ?? 
                        searchResultSelectors.id(item as SearchResult) ?? 
                        itemSelectors.id(item), 
                    type: (item as any).itemType ?? 
                        searchResultSelectors.itemType(item as SearchResult) ?? 
                        itemSelectors.type(item),
                    item: item, 
                } as SIMMItemSelected
            })

            this.$emit(
                SELECT_ITEM_MULTIPLE_MODAL_MULTIPLE_ITEMS_SELECTED,
                items
            )

            this.selectedResults = []
        }
    }

    public onItemSelected(item: SearchResult | Item, searchContent: string, currentPage: number) {
        if(this.canChainAdd && this.shouldChainAdd) {
            if(this.emitChainAddResultsTogether) {
                this.isLocallyUpdating = true
                this.selectedResults.push(item)
                // timeout to force refresh of children
                // otherwise the change false > true > false is to quick to be detected
                setTimeout(() => {
                    this.isLocallyUpdating = false
                }, 200)

                if(this.selectedResults.length > this.maxNbChainAdd) {
                    // calling close will emit the event with all the selected results
                    // because this.onHide calls this.emitMultipleItemsSelected
                    this.close()
                }
                return
            }
        }

        this.$emit(
            'item-selected', 
            { 
                id: (item as any).itemId ?? 
                    searchResultSelectors.id(item as SearchResult) ?? 
                    itemSelectors.id(item), 
                type: (item as any).itemType ?? 
                    searchResultSelectors.itemType(item as SearchResult) ?? 
                    itemSelectors.type(item),
                item: item, 
            } as SIMMItemSelected
        )
        if (!this.canChainAdd || !this.shouldChainAdd) {
            this.close()
        }
    }

    public resetSearch() {
        this.$nextTick(() => this.$refs.itemSelect?.resetSearch())
    }

    private get computedExcludedIds(): string[] {
        return this.excludedIds.concat(
            this.selectedResults.map(item => {
                return item.id
            })
        )
    }

    @Watch('shouldChainAdd')
    private onShouldChainAddChanged() {
        this.emitMultipleItemsSelected()
    }
}
